import { FC, useContext, useEffect, useState, useRef } from "react";
import { useMutation } from "react-query";
import client from "../../api";
import {
  capitalizeFirstLetter,
  convertServiceName,
  extractBillingCategories,
  getErrorMessage,
  getStatusStyle,
  logger,
} from "../../lib/utils";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { DateRangePicker } from "rsuite";
import { DateRange } from "rsuite/esm/DateRangePicker/types";
import moment from "moment";
import {
  NetworkDisconnectMsg,
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  billingTransactionsFilterOptions,
  billingTransactionSearchOptions,
  conversionFilterOptions,
  w2wTransactionFilterOptions,
  walletTerminalTransactionFilterOptions,
  transactionFilterOptions,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import {
  PaginationInput,
  User,
  TransactionsResponse,
  KwickpayHistoryResponse,
  MoneyMoversHistoryResponse,
  IMTSPaymentTransactionResponse,
} from "src/api/types";
import {
  TransactionDataType,
  UserDataType,
  CurrencyDataType,
} from "src/lib/constants/table-type";
import { Tab } from "@headlessui/react";
import classNames from "classnames";
import DepositListPage from "src/components/transactions/deposit";
import WithdrawalListPage from "src/components/transactions/withdrawal";
import KwickpayListPage from "src/components/transactions/kwickpay";
import InvoicePage from "src/components/invoice";
import Button from "src/components/ui/button";
import { BackCircleIcon } from "src/components/icons/back-circle-icon";
import UsersContainerPage from "src/components/users";
import SearchFilter from "src/components/search-filters/transactions-filter";
import SearchFilter_1 from "src/components/search-filters/billing-management-filter";
import SearchFilter_2 from "src/components/search-filters/user-management-filter_1";
import BillingSearchFilter from "src/components/search-filters/billing-search";
import MoneyMoversListPage from "src/components/transactions/billings";
import CurrencyDrop_2 from "src/components/ui/groupedDropdownList";
import MTSListPage from "src/components/transactions/mts";
import { SpinnerIcon } from "src/components/icons/spinner-icon";
import CashoutListPage from "src/components/transactions/cashout";
import ConversionListPage from "src/components/transactions/conversion";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PreLoginTransactions from "src/components/transactions/preLoginTransactions";
import WalletTransactionsPage from "src/components/transactions/walletToWallet";
import WalletTerminalTransactionsPage from "src/components/transactions/walletTerminalHistory";

interface ITransactionsManagementPage {
  isBuisiness?: boolean;
}

interface IOption {
  name: string;
  id: number;
}
interface IGroupedCategoryList {
  label: string;
  options: IOption[];
}

const TransactionsManagementPage: FC<ITransactionsManagementPage> = ({
  isBuisiness,
}) => {
  const targetModule = isBuisiness
    ? permissionConstants.TRANSACTION_HISTORY_BUSINESS
    : permissionConstants.TRANSACTION_HISTORY_INDIVIDUAL;
  const { canView, canList, canExport, canDownload } = usePermissions(targetModule);
  const canViewTransactions = canView || canList;
  const canExportTransactions = canExport || canDownload;

  const isMounted = useIsMounted();
  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
    // category: "",
    serviceName: "",
    id: null,
  });
  const [searchText, setSearchText] = useState("");
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [selectedUserID, setSelectedUserID] = useState(-1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const [invoiceData, setInvoiceData] = useState<TransactionDataType | null>(
    null,
  );
  const [currencies, setCurrencies] = useState<CurrencyDataType[]>([]);
  const [filter, setFilter] = useState("user_id");
  const [billingFilter, setBillingFilter] = useState("status");
  const [filterValue, setFilterValue] = useState("");
  const [dateRangeValue, setDateRangeValue] = useState<DateRange | null>(null);
  const [timeRangeValue, setTimeRangeValue] = useState<DateRange | null>(null);

  const now = new Date();
  const prevMonth = new Date(now.setMonth(now.getMonth() - 1));

  const [startDate, endDate] = dateRangeValue || [];
  const [startTime, endTime] = timeRangeValue || [];
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const tz = dayjs.tz.guess();
  const [formattedStartDateTime, setFormattedStartDateTime] = useState<
    string | undefined
  >("");
  const [formattedEndDateTime, setFormattedEndDateTime] = useState<
    string | undefined
  >("");
  const [error, setError] = useState<any>();
  const [categoriesList, setCategoriesList] = useState<IGroupedCategoryList[]>(
    [],
  );
  const [servicesList, setServiceList] = useState<string[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<
    IGroupedCategoryList[]
  >([]);

  const [services, setServices] = useState<any>([]);
  const [catData, setCatData] = useState<any>([]);
  const [categorySearch, setCategorySearch] = useState<string>("");

  const formatDateTime = (
    date: Date | undefined,
    time: Date | undefined,
  ): string | undefined => {
    if (date && time) {
      const combined = `${moment(date).format("YYYY-MM-DD")} ${moment(
        time,
      ).format("HH:mm")}`;
      let localTime = moment(combined, "YYYY-MM-DD HH:mm");
      const utcTime = localTime.utc();
      return utcTime.format("YYYY-MM-DD HH:mm");
    } else if (date && !time) {
      return moment(date).format("YYYY-MM-DD");
    } else if (!date && time) {
      const today = moment().format("YYYY-MM-DD");
      const localTime = moment(
        `${today} ${moment(time).format("HH:mm")}`,
        "YYYY-MM-DD HH:mm",
      );
      const utcTime = localTime.utc();

      return utcTime.format("YYYY-MM-DD HH:mm");
    }

    return undefined;
  };

  useEffect(() => {
    setFormattedStartDateTime(formatDateTime(startDate, startTime));
  }, [startDate, startTime]);

  useEffect(() => {
    setFormattedEndDateTime(formatDateTime(endDate, endTime));
  }, [endDate, endTime]);

  const [categories, setCategories] = useState<any>([]);

  const { afterToday } = DateRangePicker;

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    containerRef.current && setContainerWidth(containerRef.current.offsetWidth);
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  const onSelectedInvoiceData = (data: TransactionDataType) => {
    setInvoiceData(data);
  };

  const onBackFromInvoice = () => {
    setInvoiceData(null);
  };

  const tabLists = [
    "Deposit",
    "Withdrawals",
    "Billings",
    "Cashout",
    "Conversion",
    "Transfer",
    "Pre-login Transactions",
    "Wallet to wallet",
    "Wallet terminal",
  ];
  const tabLists_1 = [
    "Deposit",
    "Withdrawals",
    "Billings",
    "Conversion",
    "MTS",
    "Transfer",
    "Wallet to wallet",
  ];

  const getExportApi = (): ((
    input: PaginationInput,
  ) => Promise<
    | TransactionsResponse
    | KwickpayHistoryResponse
    | MoneyMoversHistoryResponse
    | IMTSPaymentTransactionResponse
  >) => {
    if (isBuisiness) {
      switch (selectedTabIndex) {
        case 1:
          return client.transactions.buisinessWithdrawal;
        case 2:
          return client.transactions.moneyMoversBuisiness;
        case 3:
          return client.conversions.exportOrderList;
        case 4:
          return client.mts.list;
        case 6:
          return client.walletToWallet.exportTransactionList;
        default:
          return client.transactions.buisinessDeposit;
      }
    }
    switch (selectedTabIndex) {
      case 1:
        return client.transactions.individualWithdrawal;
      case 2:
        return client.transactions.moneyMoversIndividual;
      case 3:
        return client.transactions.cashoutIndividual;
      case 4:
        return client.conversions.exportOrderList;
      case 7:
        return client.walletToWallet.exportTransactionList;
      case 8:
        return client.walletTerminalTransactions.exportTransactionList;
      default:
        return client.transactions.individualDeposit;
    }
  };

  const { mutate: exportTransactions, isLoading: exportDashBoardLoading } =
    useMutation(getExportApi(), {
      onSuccess: (data: any) => {
        if (data?.download_url || data?.data?.file) {
          const downloadUrl = data?.download_url || data?.data?.file;
          // Create an invisible link and trigger download
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.download = "transactions.xlsx"; // optional: customize filename
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        setAlertText("Export success");
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const exportBtn = async () => {
    if (!canExportTransactions) {
      setAlertText("You do not have permission to export transactions");
      setIsAlertOpened(true);
      return;
    }
    try {
      let formattedStartDateTime = formatDateTime(startDate, startTime);
      let formattedEndDateTime = formatDateTime(endDate, endTime);

      const exportParams: any = {
        ...searchParams,
      };

      // ❌ Do NOT send serviceName & id for Wallet Terminal export (case 8)
      if (!isBuisiness && selectedTabIndex === 8) {
        delete exportParams.serviceName;
        delete exportParams.id;
        delete exportParams.per_page;
        delete exportParams.current_page;
      }
      setTimeout(() => {
        exportTransactions({
          ...exportParams,
          search_value: searchText || undefined,
          search_field: searchText ? filter : undefined,
          start_date: formattedStartDateTime,
          end_date: formattedEndDateTime,
          ...(!isBuisiness && selectedTabIndex === 3
            ? { type: "cashout" }
            : !isBuisiness && (selectedTabIndex === 4 || selectedTabIndex === 7)
              ? {
                type: "individual",
              }
              : isBuisiness &&
                (selectedTabIndex === 3 || selectedTabIndex === 6)
                ? {
                  type: "business",
                }
                : {}),
          ...(!isBuisiness && selectedTabIndex === 8 ? {} : { export: 1 }),
        });
      }, 300);
    } catch (errInfo) {
      logger.log("Validate Failed:", errInfo);
    }
  };

  const { mutate: getCurrencies } = useMutation(client.currencies.all, {
    onSuccess: (data) => {
      setCurrencies(
        data.map((item) => ({
          key: item.id,
          ...item,
        })),
      );
    },
    onError: (error: any) => {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
      }
      setIsAlertOpened(true);
    },
  });

  const { mutate: getCategoriesList, isLoading: isCategoryLoading } =
    useMutation(client.categories.all, {
      onSuccess: (data: any) => {
        if (!data) return;
        setCatData(data);
        const gamblingCategory: any = data.find((item: any) => item.id === 47);

        const formattedCategories: IGroupedCategoryList[] = [
          {
            label: "All",
            options: [{ name: "All", id: -1 }],
          },
          ...data.map((category: any) => ({
            label: category.cat_name[2],
            options: category.services.map((service: any) => ({
              name: service.service_name[2],
              id: service.id,
            })),
          })),
        ];

        setCategoriesList(formattedCategories);
        setFilteredCategories(formattedCategories);
        setCategories(
          data
            .map((item: any) => {
              const serviceName = item.services[0]?.service_name[2];
              if (item.services.length > 0) {
                return {
                  code: extractBillingCategories(serviceName || ""),
                  name: serviceName || "",
                };
              }
              return null; // Return null for empty service cases
            })
            .filter(Boolean), // Remove `null` values
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const { mutate: getServicesList } = useMutation(client.services.all, {
    onSuccess: (data) => {
      setServiceList(["All", ...data]);
    },
    onError: (error: any) => {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
      }
      setIsAlertOpened(true);
    },
  });

  const handleWalletSelect = (value: any): void => {
    if (value === "All") {
      setSearchParams({ ...searchParams, serviceName: "", id: null });
    } else {
      setSearchParams({
        ...searchParams,
        serviceName: value.name,
        id: Number(value.id),
      });
    }
    if (error?.service) {
      setError((prev: any) => ({
        ...prev,
        serviceName: undefined,
        id: null,
      }));
    }
  };

  useEffect(() => {
    if (isMounted && canViewTransactions) {
      getCurrencies();
      getCategoriesList();
      getServicesList();
    }
  }, [isMounted, canViewTransactions]);

  useEffect(() => {
    setSearchParams({ ...searchParams, serviceName: "", id: null });
  }, [searchParams.category]);

  useEffect(() => {
    if (searchParams?.category !== "All") {
      const filterData = catData.filter(
        (item: any) =>
          extractBillingCategories(item.cat_name[2]) === searchParams?.category,
      );
      if (filterData.length > 0 && filterData[0].services?.length > 0) {
        setServices([
          "All",
          ...filterData[0].services?.map((item: any) => item.service_name[2]),
        ]);
      }
    }
  }, [categories, searchParams]);

  useEffect(() => {
    if (!isBuisiness && selectedTabIndex === 7) {
      setFilter("from_user_id");
    } else if (isBuisiness && selectedTabIndex === 6) {
      setFilter("from_user_id");
    } else {
      setFilter("user_id");
    }
    setBillingFilter("status");
    setFilterValue("");
    setDateRangeValue(null);
    setSearchText("");
    setSearchParams({ ...searchParams, serviceName: "", id: null });
  }, [selectedTabIndex]);

  useEffect(() => {
    let newList = filteredCategories
      .map((group: IGroupedCategoryList) => {
        const matchFields = group.options.filter((item: IOption) =>
          item.name.toLowerCase().includes(categorySearch.toLowerCase()),
        );
        // logger.log(matchFields, "Fwffwwe");
        if (matchFields.length > 0) {
          return {
            label: group.label,
            options: [...matchFields],
          };
        }
        return null;
      })
      .filter((group): group is IGroupedCategoryList => group !== null);
    setCategoriesList(newList);
  }, [categorySearch]);

  useEffect(() => {
    setSelectedTabIndex(0);
  }, [isBuisiness]);

  useEffect(() => {
    setSearchParams({ ...searchParams, serviceName: "", id: null });
  }, [searchParams.category]);

  useEffect(() => {
    if (searchParams?.category !== "All") {
      const filterData = catData.filter(
        (item: any) =>
          extractBillingCategories(item.cat_name[2]) === searchParams?.category,
      );
      if (filterData.length > 0 && filterData[0].services?.length > 0) {
        setServices([
          "All",
          ...filterData[0].services?.map((item: any) => item.service_name[2]),
        ]);
      }
    }
  }, [categories, searchParams]);

  useEffect(() => {
    if (!isBuisiness && selectedTabIndex === 7) {
      setFilter("from_user_id");
    } else if (isBuisiness && selectedTabIndex === 6) {
      setFilter("from_user_id");
    } else {
      setFilter("user_id");
    }
    setBillingFilter("status");
    setFilterValue("");
    setDateRangeValue(null);
    setSearchText("");
    setSearchParams({ ...searchParams, serviceName: "", id: null });
  }, [selectedTabIndex]);

  useEffect(() => {
    let newList = filteredCategories
      .map((group: IGroupedCategoryList) => {
        const matchFields = group.options.filter((item: IOption) =>
          item.name.toLowerCase().includes(categorySearch.toLowerCase()),
        );
        // logger.log(matchFields, "Fwffwwe");
        if (matchFields.length > 0) {
          return {
            label: group.label,
            options: [...matchFields],
          };
        }
        return null;
      })
      .filter((group): group is IGroupedCategoryList => group !== null);
    setCategoriesList(newList);
  }, [categorySearch]);

  useEffect(() => {
    setSelectedTabIndex(0);
  }, [isBuisiness]);

  const makeTabList = () => {
    const list: string[] = isBuisiness ? tabLists_1 : tabLists;
    const tabListsUI = list.map((item, index) => (
      <Tab
        key={`tabs_${index}`}
        className={({ selected }) =>
          classNames(
            "h-full font-medium text-[14px] md:text-[16px] relative px-2 py-2 md:px-8 md:py-4 before:absolute before:left-0 before:bottom-0 before:h-0.5 before:bg-brand before:transition-all before:duration-300 before:ease-in-out",
            "ring-transparent ring-opacity-60 ring-offset-0 ring-offset-transparent focus:outline-none focus:ring-0",
            selected
              ? "text-primary bg-white rounded-t-[8px]"
              : "text-secondary bg-transparent",
          )
        }
      >
        {item}
      </Tab>
    ));
    return (
      <Tab.List className="flex flex-row items-center w-full h-[58px] md:h-[56px]">
        {tabListsUI}
      </Tab.List>
    );
  };

  if (!canViewTransactions) {
    return (
      <div className="w-full h-full bg-gray p-6">
        <div className="bg-white rounded-lg p-6 text-center text-secondary">
          You don't have permission to view transactions history.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full flex flex-col">
        {selectedUser == null ? (
          <div
            className={`${invoiceData && "hidden"
              } w-full h-full flex-col p-2 sm:p-4`}
          >
            {" "}
            <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
              Transactions
            </p>
            <div className="mt-4 flex items-end gap-2 justify-end flex-wrap w-full mb-4">
              {selectedTabIndex === 2 ||
                (!isBuisiness && selectedTabIndex === 3) ? (
                <>
                  <div className="flex gap-2">
                    <DateRangePicker
                      format="dd/MM/yyyy"
                      size="lg"
                      block
                      placement="bottomEnd"
                      style={{ width: "308px" }}
                      value={dateRangeValue}
                      onChange={setDateRangeValue}
                      defaultCalendarValue={[prevMonth, now]}
                      shouldDisableDate={afterToday()}
                      ranges={[]}
                    />
                    <PermissionGate
                      module={[targetModule]}
                      action="can_export"
                    >
                      <button
                        type="button"
                        onClick={() => exportBtn()}
                        className="button button--type3 button--gap30 min-w-[160px]"
                        style={{ minWidth: 160 }}
                        disabled={exportDashBoardLoading}
                      >
                        {exportDashBoardLoading ? (
                          <div className="flex justify-center items-center w-full">
                            <SpinnerIcon className="h-auto w-5 animate-spin" />
                          </div>
                        ) : (
                          <>
                            Export Excel
                            <span className="btn-icon btn-icon--fill">
                              <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8.79961 1.8002L12.6996 5.4002M8.79961 1.8002L5.19961 5.4002M8.79961 1.8002L8.79961 13.2002M15.9996 8.4002V16.2002L1.59961 16.2002L1.59961 8.4002"
                                  stroke="#232800"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                ></path>
                              </svg>
                            </span>
                          </>
                        )}
                      </button>
                    </PermissionGate>
                  </div>
                </>
              ) : null}
            </div>
            <div className="mt-4 flex items-end gap-2 justify-between flex-wrap w-full mb-4">
              {selectedTabIndex === 2 ? (
                <>
                  <div className="">
                    <p className="input__name input__name--type2">Status</p>
                    <SearchFilter_1
                      value={billingFilter}
                      options={billingTransactionsFilterOptions}
                      onChange={setBillingFilter}
                      filterValue={filterValue}
                      setFilterValue={setFilterValue}
                      categories={categories}
                    />
                  </div>
                  <div className="flex gap-3">
                    <div className=" table-filter__item min-w-[320px] mb-0">
                      <p className="input__name input__name--type2">Service</p>
                      <CurrencyDrop_2
                        value={
                          searchParams?.serviceName === "" ||
                            !searchParams?.serviceName
                            ? "All"
                            : searchParams?.serviceName
                        }
                        options={categoriesList ? [...categoriesList] : []}
                        type="service"
                        onChange={handleWalletSelect}
                        categorySearch={categorySearch}
                        setCategorySearch={setCategorySearch}
                      />
                    </div>
                    <div className="">
                      <p className="input__name input__name--type2">
                        Search By Transaction Id and User Id
                      </p>
                      <BillingSearchFilter
                        options={billingTransactionSearchOptions}
                        value={filter}
                        onChange={setFilter}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        currencies={currencies}
                      />
                    </div>
                  </div>
                </>
              ) : !isBuisiness && selectedTabIndex === 3 ? (
                <>
                  <div className="">
                    <p className="input__name input__name--type2">Status</p>
                    <SearchFilter_1
                      value={billingFilter}
                      options={billingTransactionsFilterOptions}
                      onChange={setBillingFilter}
                      filterValue={filterValue}
                      setFilterValue={setFilterValue}
                      categories={categories}
                    />
                  </div>
                  <div className="flex gap-3">
                    <div className="">
                      <p className="input__name input__name--type2">
                        Search By Transaction Id and User Id
                      </p>
                      <BillingSearchFilter
                        options={billingTransactionSearchOptions}
                        value={filter}
                        onChange={setFilter}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        currencies={currencies}
                      />
                    </div>
                  </div>
                </>
              ) : !isBuisiness && selectedTabIndex === 4 ? (
                <>
                  <SearchFilter
                    options={conversionFilterOptions}
                    value={filter}
                    onChange={setFilter}
                    searchText={searchText}
                    setSearchText={setSearchText}
                    currencies={currencies}
                  />
                  <div className="flex gap-2">
                    <DateRangePicker
                      format="dd/MM/yyyy"
                      size="lg"
                      block
                      placement="bottomEnd"
                      style={{ width: "308px" }}
                      value={dateRangeValue}
                      onChange={setDateRangeValue}
                      defaultCalendarValue={[prevMonth, now]}
                      shouldDisableDate={afterToday()}
                      ranges={[]}
                    />
                    <PermissionGate
                      module={[targetModule]}
                      action="can_export"
                    >
                      <button
                        type="button"
                        onClick={() => exportBtn()}
                        className="button button--type3 button--gap30 min-w-[160px]"
                        style={{ minWidth: 160 }}
                        disabled={exportDashBoardLoading}
                      >
                        {exportDashBoardLoading ? (
                          <div className="flex justify-center items-center w-full">
                            <SpinnerIcon className="h-auto w-5 animate-spin" />
                          </div>
                        ) : (
                          <>
                            Export Excel
                            <span className="btn-icon btn-icon--fill">
                              <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8.79961 1.8002L12.6996 5.4002M8.79961 1.8002L5.19961 5.4002M8.79961 1.8002L8.79961 13.2002M15.9996 8.4002V16.2002L1.59961 16.2002L1.59961 8.4002"
                                  stroke="#232800"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                ></path>
                              </svg>
                            </span>
                          </>
                        )}
                      </button>
                    </PermissionGate>
                  </div>
                </>
              ) : isBuisiness && selectedTabIndex === 4 ? (
                <>
                  <SearchFilter_2
                    options={[]}
                    placeholder="Search by Id,Request Id, Channel, Sender Name, Sender....."
                    value={filter}
                    onChange={setFilter}
                    searchText={searchText}
                    setSearchText={setSearchText}
                  />
                  <div className="flex gap-2">
                    <DateRangePicker
                      format="dd/MM/yyyy"
                      size="lg"
                      block
                      placement="bottomEnd"
                      style={{ width: "308px" }}
                      value={dateRangeValue}
                      onChange={setDateRangeValue}
                      defaultCalendarValue={[prevMonth, now]}
                      shouldDisableDate={afterToday()}
                      ranges={[]}
                    />

                    <PermissionGate
                      module={[targetModule]}
                      action="can_export"
                    >
                      <button
                        type="button"
                        onClick={() => exportBtn()}
                        className="button button--type3 button--gap30 min-w-[160px]"
                        style={{ minWidth: 160 }}
                        disabled={exportDashBoardLoading}
                      >
                        {exportDashBoardLoading ? (
                          <div className="flex justify-center items-center w-full">
                            <SpinnerIcon className="h-auto w-5 animate-spin" />
                          </div>
                        ) : (
                          <>
                            Export Excel
                            <span className="btn-icon btn-icon--fill">
                              <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8.79961 1.8002L12.6996 5.4002M8.79961 1.8002L5.19961 5.4002M8.79961 1.8002L8.79961 13.2002M15.9996 8.4002V16.2002L1.59961 16.2002L1.59961 8.4002"
                                  stroke="#232800"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                ></path>
                              </svg>
                            </span>
                          </>
                        )}
                      </button>
                    </PermissionGate>
                  </div>
                </>
              ) : !isBuisiness && selectedTabIndex === 7 ? (
                <>
                  <SearchFilter
                    options={w2wTransactionFilterOptions}
                    value={filter}
                    onChange={setFilter}
                    searchText={searchText}
                    setSearchText={setSearchText}
                    currencies={currencies}
                  />
                  <div className="flex gap-2">
                    <DateRangePicker
                      format="dd/MM/yyyy"
                      size="lg"
                      block
                      placement="bottomEnd"
                      style={{ width: "308px" }}
                      value={dateRangeValue}
                      onChange={setDateRangeValue}
                      defaultCalendarValue={[prevMonth, now]}
                      shouldDisableDate={afterToday()}
                      ranges={[]}
                    />

                    <PermissionGate
                      module={[targetModule]}
                      action="can_export"
                    >
                      <button
                        type="button"
                        onClick={() => exportBtn()}
                        className="button button--type3 button--gap30 min-w-[160px]"
                        style={{ minWidth: 160 }}
                        disabled={exportDashBoardLoading}
                      >
                        {exportDashBoardLoading ? (
                          <div className="flex justify-center items-center w-full">
                            <SpinnerIcon className="h-auto w-5 animate-spin" />
                          </div>
                        ) : (
                          <>
                            Export Excel
                            <span className="btn-icon btn-icon--fill">
                              <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8.79961 1.8002L12.6996 5.4002M8.79961 1.8002L5.19961 5.4002M8.79961 1.8002L8.79961 13.2002M15.9996 8.4002V16.2002L1.59961 16.2002L1.59961 8.4002"
                                  stroke="#232800"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                ></path>
                              </svg>
                            </span>
                          </>
                        )}
                      </button>
                    </PermissionGate>
                  </div>
                </>
              ) : isBuisiness && selectedTabIndex === 6 ? (
                <>
                  <SearchFilter
                    options={w2wTransactionFilterOptions}
                    value={filter}
                    onChange={setFilter}
                    searchText={searchText}
                    setSearchText={setSearchText}
                    currencies={currencies}
                  />
                  <div className="flex gap-2">
                    <DateRangePicker
                      format="dd/MM/yyyy"
                      size="lg"
                      block
                      placement="bottomEnd"
                      style={{ width: "308px" }}
                      value={dateRangeValue}
                      onChange={setDateRangeValue}
                      defaultCalendarValue={[prevMonth, now]}
                      shouldDisableDate={afterToday()}
                      ranges={[]}
                    />

                    <PermissionGate
                      module={[targetModule]}
                      action="can_export"
                    >
                      <button
                        type="button"
                        onClick={() => exportBtn()}
                        className="button button--type3 button--gap30 min-w-[160px]"
                        style={{ minWidth: 160 }}
                        disabled={exportDashBoardLoading}
                      >
                        {exportDashBoardLoading ? (
                          <div className="flex justify-center items-center w-full">
                            <SpinnerIcon className="h-auto w-5 animate-spin" />
                          </div>
                        ) : (
                          <>
                            Export Excel
                            <span className="btn-icon btn-icon--fill">
                              <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8.79961 1.8002L12.6996 5.4002M8.79961 1.8002L5.19961 5.4002M8.79961 1.8002L8.79961 13.2002M15.9996 8.4002V16.2002L1.59961 16.2002L1.59961 8.4002"
                                  stroke="#232800"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                ></path>
                              </svg>
                            </span>
                          </>
                        )}
                      </button>
                    </PermissionGate>
                  </div>
                </>
              ) : !isBuisiness && selectedTabIndex === 8 ? (
                <>
                  <SearchFilter
                    options={walletTerminalTransactionFilterOptions}
                    value={filter}
                    onChange={setFilter}
                    searchText={searchText}
                    setSearchText={setSearchText}
                    currencies={currencies}
                  />
                  <div className="flex gap-2">
                    <DateRangePicker
                      format="dd/MM/yyyy"
                      size="lg"
                      block
                      placement="bottomEnd"
                      style={{ width: "308px" }}
                      value={dateRangeValue}
                      onChange={setDateRangeValue}
                      defaultCalendarValue={[prevMonth, now]}
                      shouldDisableDate={afterToday()}
                      ranges={[]}
                    />

                    <PermissionGate
                      module={[targetModule]}
                      action="can_export"
                    >
                      <button
                        type="button"
                        onClick={() => exportBtn()}
                        className="button button--type3 button--gap30 min-w-[160px]"
                        style={{ minWidth: 160 }}
                        disabled={exportDashBoardLoading}
                      >
                        {exportDashBoardLoading ? (
                          <div className="flex justify-center items-center w-full">
                            <SpinnerIcon className="h-auto w-5 animate-spin" />
                          </div>
                        ) : (
                          <>
                            Export Excel
                            <span className="btn-icon btn-icon--fill">
                              <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8.79961 1.8002L12.6996 5.4002M8.79961 1.8002L5.19961 5.4002M8.79961 1.8002L8.79961 13.2002M15.9996 8.4002V16.2002L1.59961 16.2002L1.59961 8.4002"
                                  stroke="#232800"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                ></path>
                              </svg>
                            </span>
                          </>
                        )}
                      </button>
                    </PermissionGate>
                  </div>
                </>
              ) : (
                selectedTabIndex !== 2 && (
                  <>
                    <SearchFilter
                      options={transactionFilterOptions}
                      value={filter}
                      onChange={setFilter}
                      searchText={searchText}
                      setSearchText={setSearchText}
                      currencies={currencies}
                    />

                    <div className="flex gap-2">
                      <DateRangePicker
                        format="dd/MM/yyyy"
                        size="lg"
                        block
                        placement="bottomEnd"
                        style={{ width: "308px" }}
                        value={dateRangeValue}
                        onChange={setDateRangeValue}
                        defaultCalendarValue={[prevMonth, now]}
                        shouldDisableDate={afterToday()}
                        ranges={[]}
                      />

                      <PermissionGate
                        module={[targetModule]}
                        action="can_export"
                      >
                        <button
                          type="button"
                          onClick={() => exportBtn()}
                          className="button button--type3 button--gap30 min-w-[160px]"
                          style={{ minWidth: 160 }}
                          disabled={exportDashBoardLoading}
                        >
                          {exportDashBoardLoading ? (
                            <div className="flex justify-center items-center w-full">
                              <SpinnerIcon className="h-auto w-5 animate-spin" />
                            </div>
                          ) : (
                            <>
                              Export Excel
                              <span className="btn-icon btn-icon--fill">
                                <svg
                                  width="17"
                                  height="17"
                                  viewBox="0 0 17 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M8.79961 1.8002L12.6996 5.4002M8.79961 1.8002L5.19961 5.4002M8.79961 1.8002L8.79961 13.2002M15.9996 8.4002V16.2002L1.59961 16.2002L1.59961 8.4002"
                                    stroke="#232800"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  ></path>
                                </svg>
                              </span>
                            </>
                          )}
                        </button>
                      </PermissionGate>
                    </div>
                  </>
                )
              )}
            </div>
            <Tab.Group
              selectedIndex={selectedTabIndex}
              onChange={(tabIdx: number) => setSelectedTabIndex(tabIdx)}
            >
              {makeTabList()}
              <Tab.Panels>
                <Tab.Panel className="border-0 focus:border-0 focus:ring-0 ring-0">
                  <DepositListPage
                    searchText={searchText}
                    onSelectedInvoiceData={onSelectedInvoiceData}
                    onSelectedUser={setSelectedUser}
                    searchField={filter}
                    startDate={startDate}
                    endDate={endDate}
                    timezone={tz}
                    isBuisiness={isBuisiness}
                  />
                </Tab.Panel>
                <Tab.Panel className="border-0 focus:border-0 focus:ring-0 ring-0">
                  <WithdrawalListPage
                    searchText={searchText}
                    onSelectedInvoiceData={onSelectedInvoiceData}
                    onSelectedUser={setSelectedUser}
                    searchField={filter}
                    startDate={startDate}
                    endDate={endDate}
                    timezone={tz}
                    isBuisiness={isBuisiness}
                  />
                </Tab.Panel>
                <Tab.Panel className="border-0 focus:border-0 focus:ring-0 ring-0">
                  <MoneyMoversListPage
                    searchParams={searchParams}
                    searchText={searchText}
                    // setSearchText
                    setSearchParams={setSearchParams}
                    onSelectedInvoiceData={onSelectedInvoiceData}
                    onSelectedUser={setSelectedUser}
                    searchField={filter}
                    filterValue={filterValue}
                    startDate={formattedStartDateTime}
                    endDate={formattedEndDateTime}
                    timezone={tz}
                    isBuisiness={isBuisiness}
                    billingFilter={billingFilter}
                  />
                </Tab.Panel>
                {!isBuisiness && (
                  <Tab.Panel className="border-0 focus:border-0 focus:ring-0 ring-0">
                    <CashoutListPage
                      searchParams={searchParams}
                      searchText={searchText}
                      // setSearchText
                      setSearchParams={setSearchParams}
                      onSelectedInvoiceData={onSelectedInvoiceData}
                      onSelectedUser={setSelectedUser}
                      searchField={filter}
                      filterValue={filterValue}
                      startDate={formattedStartDateTime}
                      endDate={formattedEndDateTime}
                      timezone={tz}
                      isBuisiness={isBuisiness}
                      billingFilter={billingFilter}
                    />
                  </Tab.Panel>
                )}
                <Tab.Panel className="border-0 focus:border-0 focus:ring-0 ring-0">
                  <ConversionListPage
                    searchText={searchText}
                    onSelectedInvoiceData={onSelectedInvoiceData}
                    onSelectedUser={setSelectedUser}
                    searchField={filter}
                    startDate={startDate}
                    endDate={endDate}
                    timezone={tz}
                    isBuisiness={isBuisiness}
                  />
                </Tab.Panel>

                {isBuisiness && (
                  <Tab.Panel className="border-0 focus:border-0 focus:ring-0 ring-0">
                    <MTSListPage
                      searchText={searchText}
                      onSelectedInvoiceData={onSelectedInvoiceData}
                      onSelectedUser={setSelectedUser}
                      searchField={filter}
                      startDate={startDate}
                      endDate={endDate}
                      timezone={tz}
                      isBuisiness={isBuisiness}
                    />
                  </Tab.Panel>
                )}
                <Tab.Panel className="border-0 focus:border-0 focus:ring-0 ring-0">
                  <KwickpayListPage
                    searchText={searchText}
                    onSelectedInvoiceData={onSelectedInvoiceData}
                    onSelectedUser={setSelectedUser}
                    searchField={filter}
                    startDate={startDate}
                    timezone={tz}
                    endDate={endDate}
                    isBuisiness={isBuisiness}
                  />
                </Tab.Panel>
                {!isBuisiness && (
                  <Tab.Panel className="border-0 focus:border-0 focus:ring-0 ring-0">
                    <PreLoginTransactions
                      searchParams={searchParams}
                      searchText={searchText}
                      // setSearchText
                      setSearchParams={setSearchParams}
                      onSelectedInvoiceData={onSelectedInvoiceData}
                      onSelectedUser={setSelectedUser}
                      searchField={filter}
                      filterValue={filterValue}
                      startDate={formattedStartDateTime}
                      endDate={formattedEndDateTime}
                      timezone={tz}
                      isBuisiness={isBuisiness}
                      billingFilter={billingFilter}
                    />
                  </Tab.Panel>
                )}
                <Tab.Panel className="border-0 focus:border-0 focus:ring-0 ring-0">
                  <WalletTransactionsPage
                    searchText={searchText}
                    searchField={filter}
                    startDate={startDate}
                    endDate={endDate}
                    timezone={tz}
                    isBuisiness={isBuisiness}
                  />
                </Tab.Panel>

                {!isBuisiness && (
                  <Tab.Panel className="border-0 focus:border-0 focus:ring-0 ring-0">
                    <WalletTerminalTransactionsPage
                      searchText={searchText}
                      searchField={filter}
                      startDate={startDate}
                      endDate={endDate}
                      timezone={tz}
                      isBuisiness={isBuisiness}
                    />
                  </Tab.Panel>
                )}
              </Tab.Panels>
            </Tab.Group>
          </div>
        ) : (
          <div
            className={`${invoiceData && "hidden"
              } flex flex-col w-full p-2 sm:p-4`}
          >
            <div className="flex flex-row items-center justify-between sm:justify-start gap-4">
              <div className="flex flex-row items-center gap-2">
                <Button
                  variant="icon"
                  className="w-[24px] bg-transparent h-[24px]"
                  onClick={() => {
                    setSelectedUserID(-1);
                    setSelectedUser(null);
                  }}
                >
                  <BackCircleIcon className="w-[24px] h-[24px] text-primary" />
                </Button>
                <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[38px] sm:leading-[48px]">
                  {!selectedUser.user_data.first_name &&
                    !selectedUser.user_data.last_name
                    ? "-"
                    : (selectedUser.user_data.first_name ?? " ") +
                    " " +
                    (selectedUser.user_data.last_name ?? " ")}
                </p>
              </div>
              <p
                className={`font-manrope text-[12px] font-medium px-2 rounded-full ${getStatusStyle(
                  selectedUser.status.name,
                )}`}
              >
                {capitalizeFirstLetter(selectedUser.status.name)}
              </p>
            </div>
            <UsersContainerPage
              user={selectedUser}
              onSelectedInvoiceData={onSelectedInvoiceData}
            />
          </div>
        )}
      </div>
      {invoiceData && (
        <InvoicePage
          data={invoiceData!}
          callback={onBackFromInvoice}
          canDownload={canDownload}
        />
      )}
    </div>
  );
};

export default TransactionsManagementPage;
